import {
  InputDescription,
  InputHint,
} from "@design-system/components/Form/Utils";
import { Textarea } from "@design-system/ui/textarea";
import { cn } from "@design-system/utils";
import {
  lengthRule,
  maxLengthRule,
  minLengthRule,
  requiredRule,
  useFormControl,
} from "@mongez/react-form";
import { useEffect, useRef, useState } from "react";
import type { BaseInputProps } from "./BaseInput";
import { InputError } from "./InputError";
import { InputLabel } from "./InputLabel";

type TextareaInputProps = BaseInputProps & {
  /**
   * If true, the textarea will expand to fit the content
   * @default true
   */
  autoExpand?: boolean;
  /**
   * Show character count
   * @default false
   */
  showCount?: boolean;
};

export function TextareaInput({
  showCount,
  autoExpand = true,
  ...props
}: TextareaInputProps) {
  const {
    changeValue,
    value = "",
    error,
    id,
    disabled,
    inputRef,
    otherProps,
  } = useFormControl({
    rules: [requiredRule, minLengthRule, maxLengthRule, lengthRule],
    ...props,
  });

  const [charCount, setCharCount] = useState(value.length);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea || !autoExpand) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";
    // Set the height to scrollHeight to expand the textarea
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useEffect(() => {
    adjustHeight();
  }, [value, autoExpand]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setCharCount(newValue.length);
    changeValue(newValue);
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      <div className="flex flex-col gap-1">
        {props.label && (
          <InputLabel htmlFor={id} required={props.required}>
            {props.label}
          </InputLabel>
        )}
        {props.description && (
          <InputDescription>{props.description}</InputDescription>
        )}
      </div>
      <div className="relative">
        <Textarea
          value={value}
          onChange={handleChange}
          id={id}
          ref={(el) => {
            // Handle both refs
            textareaRef.current = el;
            if (typeof inputRef === "function") {
              inputRef(el);
            } else if (inputRef) {
              inputRef.current = el;
            }
          }}
          disabled={disabled}
          className={cn(showCount && "pb-8")}
          style={{
            resize: autoExpand ? "none" : "vertical",
            overflow: autoExpand ? "hidden" : "auto",
            minHeight: "80px",
          }}
          {...otherProps}
        />
        {showCount && (
          <div className="absolute bottom-2 right-2 bg-background dark:bg-gray-800 px-1 text-xs text-muted-foreground dark:text-gray-400">
            {charCount}
            {props.maxLength && ` / ${props.maxLength}`}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {props.hint && !error && <InputHint>{props.hint}</InputHint>}
        <InputError error={error} />
      </div>
    </div>
  );
}
